import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { startOfDay, subDays } from "date-fns";
import { DateRange } from "@daypicker/react";
import persistReducer from "redux-persist/es/persistReducer";
import storage from "redux-persist/es/storage";

// Define the Screen type (adjust it according to your data structure)
interface Screen {
	id: string;
	business_name?: string;
	name?: string;
	full_address?: string;
	short_address?: string;
	impressions?: number;
	latitude: number;
	longitude: number;
}

interface FilterState {
	pageNo: number;
	perPage: number;
	pageOrderColumn: string;
	pageOrderDir: string;
	agencyFilterValue: string[];
	statusValue: string;
	date: DateRange | undefined;
	screenData: Screen[];
	categoryFilterValue: string;
	assetDate: DateRange | undefined;
}
const getInitialFilterState = (): FilterState => {
	const initialFromDate = startOfDay(subDays(new Date(), 63));
	const initialToDate = new Date();

	return {
		pageNo: 1,
		perPage: 100,
		pageOrderColumn: "o.create_time",
		pageOrderDir: "desc",
		agencyFilterValue: ["-"],
		statusValue: "GLOBAL",
		date: {
			from: initialFromDate,
			to: initialToDate,
		},
		screenData: [],
		categoryFilterValue: "",
		assetDate: {
			from: initialFromDate,
			to: initialToDate,
		},
	};
};

const initialState: FilterState = getInitialFilterState();

const filterSlice = createSlice({
	name: "filter",
	initialState,
	reducers: {
		setAgencyFilterValue(state, action: PayloadAction<string[]>) {
			state.agencyFilterValue = action.payload;
		},
		setStatusValue(state, action: PayloadAction<string>) {
			state.statusValue = action.payload;
		},
		setDate(state, action: PayloadAction<DateRange | undefined>) {
			state.date = action.payload;
		},
		setAssetDate(state, action: PayloadAction<DateRange | undefined>) {
			state.assetDate = action.payload;
		},
		setPerPage(state, action: PayloadAction<number>) {
			state.perPage = action.payload;
		},
		setPageNo(state, action: PayloadAction<number>) {
			state.pageNo = action.payload;
		},
		setPageOrderColumn(state, action: PayloadAction<string>) {
			state.pageOrderColumn = action.payload;
		},
		setPageOrderDir(state, action: PayloadAction<string>) {
			state.pageOrderDir = action.payload;
		},
		setScreenData(state, action) {
			state.screenData = action.payload;
		},
		setCategoryFilterValue(state, action: PayloadAction<string>) {
			state.categoryFilterValue = action.payload;
		},
		resetFilters(state) {
			state.agencyFilterValue = initialState.agencyFilterValue;
			state.date = initialState.date;
			state.perPage = initialState.perPage;
		},
		resetOrderListingFilters(state) {
			const defaultState = getInitialFilterState();

			state.pageNo = defaultState.pageNo;
			state.perPage = defaultState.perPage;
			state.pageOrderColumn = defaultState.pageOrderColumn;
			state.pageOrderDir = defaultState.pageOrderDir;
			state.agencyFilterValue = defaultState.agencyFilterValue;
			state.statusValue = defaultState.statusValue;
			state.date = defaultState.date;
		},
		resetFiltersForLogout() {
			return getInitialFilterState();
		},
	},
});

// Persist configuration for the filter slice
const filterPersistConfig = {
	key: "filter",
	storage,
};

// Persisted reducer for the filter slice
export const agencyFiltersPersistentReducer = persistReducer(filterPersistConfig, filterSlice.reducer);

export const {
	setAgencyFilterValue,
	setStatusValue,
	setDate,
	setPerPage,
	setPageNo,
	setPageOrderColumn,
	setPageOrderDir,
	setScreenData,
	setCategoryFilterValue,
	setAssetDate,
	resetFilters,
	resetOrderListingFilters,
	resetFiltersForLogout,
} = filterSlice.actions;
